/**
 * Site Analytics Tracking Hook
 * Tracks all visitors (logged in or not) for admin analytics
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate or retrieve a persistent visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('ov_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('ov_visitor_id', visitorId);
  }
  return visitorId;
};

// Build axios auth config if a token is available (attaches user_id server-side)
const authConfig = () => {
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

export const useAnalyticsTracking = () => {
  const sessionIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const lastTrackedPath = useRef(null);
  const location = useLocation();

  const trackVisit = useCallback(async () => {
    try {
      const visitorId = getVisitorId();
      const response = await axios.post(`${API}/analytics/track-visit`, {
        visitor_id: visitorId,
        page_path: window.location.pathname,
        referrer: document.referrer || null
      }, authConfig());
      
      if (response.data.session_id) {
        sessionIdRef.current = response.data.session_id;
        startTimeRef.current = Date.now();
        lastTrackedPath.current = window.location.pathname;
      }
    } catch (error) {
      // Silently fail - analytics should not break the app
      console.debug('Analytics tracking failed:', error.message);
    }
  }, []);

  const trackPageView = useCallback(async (pagePath) => {
    if (!sessionIdRef.current || pagePath === lastTrackedPath.current) return;
    
    try {
      await axios.post(`${API}/analytics/track-page`, {
        visitor_id: getVisitorId(),
        session_id: sessionIdRef.current,
        page_path: pagePath
      }, authConfig());
      lastTrackedPath.current = pagePath;
    } catch (error) {
      console.debug('Page tracking failed:', error.message);
    }
  }, []);

  const sendHeartbeat = useCallback(async () => {
    if (!sessionIdRef.current || !startTimeRef.current) return;
    
    try {
      const durationSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      await axios.post(`${API}/analytics/heartbeat`, {
        visitor_id: getVisitorId(),
        session_id: sessionIdRef.current,
        duration_seconds: durationSeconds
      }, authConfig());
    } catch (error) {
      // Silently fail
      console.debug('Analytics heartbeat failed:', error.message);
    }
  }, []);

  // Track initial visit
  useEffect(() => {
    trackVisit();

    // iter_474 — Heartbeat every 30s while focused. While the tab is
    // hidden we explicitly send ONE final heartbeat (in the
    // visibilitychange handler below) and then skip the interval —
    // that gives accurate session duration without burning radio /
    // CPU every 30s on a backgrounded tab.
    heartbeatIntervalRef.current = setInterval(() => {
      if (document.hidden) return;
      sendHeartbeat();
    }, 30000);

    // Send heartbeat on page visibility change (when user leaves/returns)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        sendHeartbeat();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Send heartbeat before page unload
    const handleBeforeUnload = () => {
      sendHeartbeat();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // Cleanup
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Final heartbeat on unmount
      sendHeartbeat();
    };
  }, [trackVisit, sendHeartbeat]);

  // Track page changes
  useEffect(() => {
    if (sessionIdRef.current) {
      trackPageView(location.pathname);
    }
  }, [location.pathname, trackPageView]);

  return { visitorId: getVisitorId() };
};

export default useAnalyticsTracking;
